import axios from 'axios'
import { TFunction } from 'i18next'
import i18next from 'i18next'
import merge from 'lodash/merge'

import { message } from '@/components'
import { errorMsg } from '@/consts/errorMsg'

const DEFAULT_TYPE: 'default' | 'success' | 'error' = 'error'

export function reportError(error: any, msg: string, params: any) {
  if (error) {
    try {
      console.error(error, msg, params)
    } catch (error) {
      // do nothing
    }
  }
}

export function getErrorCodeMessage(code: number, t: TFunction = i18next.t): string {
  const fallback = (errorMsg as Record<number, string>)[code]
  return t(`common.errorCodes.${code}`, {
    defaultValue: fallback ?? t('common.errorCodes.default'),
  })
}

export function showError(msg: string, code = 'default', type = DEFAULT_TYPE, source = 'platform') {
  if (source === 'platform') {
    msg = i18next.t(`common.errorCodes.${code}`, { defaultValue: msg })
  } else {
    msg = i18next.t(`common.errorCodes.${source}.${code}`, { defaultValue: msg })
  }
  message.open({
    content: msg,
    type,
  })
}

export function formatErrCode(errCodeMap: Record<string, any> = {}) {
  if (typeof errCodeMap === 'string') {
    errCodeMap = {
      default: {
        msg: i18next.t(`common.errorCodes.${errCodeMap}`, { defaultValue: errCodeMap }),
        type: DEFAULT_TYPE,
      },
    }
  }

  const result = merge(
    {
      ...errorMsg,
      7: {
        msg: i18next.t('common.errorCodes.7', { defaultValue: errorMsg[7] }),
        needLog: false,
        type: 'error',
      },
      default: {
        msg: i18next.t('common.errorCodes.default', { defaultValue: 'Network error' }),
        type: DEFAULT_TYPE,
      },
    },
    errCodeMap,
  )

  function normalizeEntry(value: any): any {
    if (typeof value === 'string') {
      return {
        msg: i18next.t(`common.errorCodes.${value}`, { defaultValue: value }),
        type: DEFAULT_TYPE,
      }
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      const keys = Object.keys(value)
      const hasNumericKeys = keys.some((k) => /^[0-9]+$/.test(k))

      if (hasNumericKeys) {
        const result: Record<string, any> = {}
        for (const [k, v] of Object.entries(value)) {
          if (/^[0-9]+$/.test(k)) {
            result[k] = normalizeEntry(v)
          }
        }
        return result
      }
    }

    return Object.assign(
      {
        msg: '',
        type: DEFAULT_TYPE,
      },
      value,
    )
  }

  return Object.keys(result).reduce<Record<string, any>>((res, key) => {
    res[key] = normalizeEntry(result[key])
    return res
  }, {})
}

export function handleRequestError(error: any, showMsg = true) {
  if (error.code === -7) {
    return error
  }

  reportError(
    error,
    error.message,
    error.config
      ? {
          url: error.config.url,
          method: error.config.method,
          data: error.config.data,
        }
      : {},
  )
  showMsg && showError('default')
  return { code: -2, msg: error.message }
}

export function handleResponseError(error: any, showMsg = true) {
  if (axios.isCancel(error)) {
    return {
      code: -5,
      msg: i18next.t('common.errorCodes.-5', { defaultValue: 'Request canceled' }),
      __CANCEL__: true,
    }
  }

  // https://github.com/axios/axios/issues/383
  if (!!error.isAxiosError && !error.response) {
    showMsg && showError('default')
    return { code: -4, msg: error.message }
  }

  if (error.response) {
    if (error.response.status === 429) {
      const code = -6
      if (error._matErrMap && error._matErrMap[code]) {
        showMsg && showError(error._matErrMap[code].msg)
      }
      return { code, msg: error.message }
    }
  }

  reportError(
    error,
    error.msg,
    error.config
      ? {
          url: error.config.url,
          method: error.config.method,
          data: error.config.data,
        }
      : {},
  )
  showMsg && showError('default')

  return { code: -3, msg: error.message }
}
